import { DataTransferStatusSimple } from '@/components/common/data-transfer-status-simple';
import { UserApprovalPage } from '@/pages/admin/user-approval-page';
import { OrganizationManagement } from '@/components/features/management/organization-management';
import { StationManagement } from '@/components/features/management/station-management';
import { UserManagement } from '@/components/features/management/user-management';
import { RoleSetList } from '@/components/features/menu-role-set/role-set-list';
import { AccountManagement } from '@/components/features/master/account-master';
import { CarManagement } from '@/components/features/master/car-master';
import { CustCardManagement } from '@/components/features/master/cust-card-master';
import { CustCostManagement } from '@/components/features/master/cust-cost-master';
import { CustManagement } from '@/components/features/master/cust-master';
import { NozzleManagement } from '@/components/features/master/nozzle-master';
import { OilManagement } from '@/components/features/master/oil-master';
import { TankManagement } from '@/components/features/master/tank-master';
import { Aggregate } from '@/components/features/sales/aggregate';
import { CardSales } from '@/components/features/sales/aggregation/cardsales';
import { Delivery } from '@/components/features/sales/aggregation/delivery';
import { Oct } from '@/components/features/sales/aggregation/oct';
import { SalesInfo } from '@/components/features/sales/aggregation/salesinfo';
import { SalesTotal } from '@/components/features/sales/aggregation/salestotal';
import { SelfSales } from '@/components/features/sales/aggregation/selfsales';
import { TimeSale } from '@/components/features/sales/aggregation/timesale';
import { CarWash } from '@/components/features/sales/car-wash';
import { CloseStat } from '@/components/features/sales/close-stat';
import { Money } from '@/components/features/sales/inout/money';
import { Invoice } from '@/components/features/sales/invoice/invoice';
import { TaxInvoice } from '@/components/features/sales/tax-invoice/tax-invoice';
import { Purchase } from '@/components/features/sales/inout/purchase';
import { TankJg } from '@/components/features/stock/tankjg';
import { TlgRealtime } from '@/components/features/stock/tlg-realtime';
import { DashboardPage } from '@/pages/dashboard-page';
import { Code } from 'lucide-react';
import { memo } from 'react';
import type { Tab } from './tab-header';

type OpenTabHandler = (item: Tab) => void;

interface TabContentProps {
  tabs: Tab[];
  activeTab: Tab | null;
  onOpenTab?: OpenTabHandler;
}

type TabComponent = React.ComponentType<{ onOpenTab?: OpenTabHandler }>;

// 탭 id → 화면 컴포넌트. 여기 없는 id는 아래 placeholder로 렌더링된다. (알림 딥링크 해석에도 쓴다)
export const TAB_COMPONENTS: Record<string, TabComponent> = {
  dashboard: DashboardPage,
  'data-transfer-status': DataTransferStatusSimple,
  'user-management': UserManagement,
  'organization-management': OrganizationManagement,
  'station-management': StationManagement,
  'user-approval': UserApprovalPage,
  'menu-role-sets': RoleSetList,
  account: AccountManagement,
  nozzle: NozzleManagement,
  car: CarManagement,
  cust: CustManagement,
  'cust-card': CustCardManagement,
  oil: OilManagement,
  'cust-cost': CustCostManagement,
  tank: TankManagement,
  salesinfo: SalesInfo,
  selfsales: SelfSales,
  cardsales: CardSales,
  delivery: Delivery,
  oct: Oct,
  salestotal: SalesTotal,
  timesale: TimeSale,
  money: Money,
  purchase: Purchase,
  tankjg: TankJg,
  'tlg-realtime': TlgRealtime,
  'close-stat': CloseStat,
  aggregate: Aggregate,
  'car-wash': CarWash,
  invoice: Invoice,
  'tax-invoice': TaxInvoice,
};

// 탭 전환(activeTab 변경)으로 TabContent가 재렌더돼도 비활성 탭 화면은 다시 그리지 않는다.
// 화면 컴포넌트는 자체 state/effect로만 갱신되므로 props(component, onOpenTab)가 같으면 건너뛰어도 안전하다.
const TabPane = memo(function TabPane({
  component: Component,
  onOpenTab,
}: {
  component: TabComponent;
  onOpenTab?: OpenTabHandler;
}) {
  return <Component onOpenTab={onOpenTab} />;
});

// 열린 탭(tabs)은 닫기 전까지 마운트 상태를 유지해 화면 state를 보존하고, 닫으면 언마운트된다.
export function TabContent({ tabs, activeTab, onOpenTab }: TabContentProps) {
  if (!activeTab) {
    return (
      <div className='flex-1 flex items-center justify-center text-muted-foreground'>
        <div className='text-center'>
          <Code className='size-12 mx-auto mb-4 opacity-50' />
          <h3 className='text-lg font-medium mb-2'>열린 화면이 없습니다</h3>
          <p className='text-sm'>왼쪽 메뉴에서 화면을 선택하세요</p>
        </div>
      </div>
    );
  }

  return (
    <div className='flex-1 overflow-auto'>
      {tabs.map(tab => {
        const Component = TAB_COMPONENTS[tab.id];
        if (!Component) return null;
        return (
          <div key={tab.id} className={activeTab.id === tab.id ? 'h-full' : 'hidden'}>
            <TabPane component={Component} onOpenTab={onOpenTab} />
          </div>
        );
      })}

      {!TAB_COMPONENTS[activeTab.id] && (
        <div className='p-6'>
          <div className='flex items-center gap-3 mb-6'>
            <activeTab.icon className='size-6' />
            <h1 className='text-2xl font-bold'>{activeTab.title}</h1>
          </div>
          <div className='p-8 border-2 border-dashed border-muted-foreground/25 rounded-lg text-center'>
            <activeTab.icon className='size-12 mx-auto mb-4 opacity-50' />
            <h3 className='text-lg font-medium mb-2'>
              {activeTab.title} Content
            </h3>
            <p className='text-muted-foreground'>
              This is the content area for {activeTab.title.toLowerCase()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
